import { httpRequest } from "@services/http";

const BASE = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/delivery/`;

export interface ProviderAccount {
  id: number;
  organization_id: number;
  provider: string;
  name: string;
  is_active: boolean;
  has_credentials: boolean;
  // "test" or "live", or null when no key is set. Which environment an account points at is
  // the question people actually ask, and the key itself is never returned.
  key_environment: string | null;
  // False means this account uses the platform's Lob key, which is the normal case.
  uses_own_key: boolean;
  webhook_configured: boolean;
  // The organisation's default return address. Campaigns inherit it unless they set their
  // own, so it is typed once rather than on every card.
  from_address?: Record<string, string>;
}

export interface PostalAddress {
  name: string;
  address_line1: string;
  address_line2?: string;
  address_city: string;
  address_state: string;
  address_zip: string;
}

export interface TestSendPiece {
  ad_id?: number | null;
  size?: string;
  // Plain fields still work server to server. A browser must use the encoded pair: a WAF rule
  // rejects CSS in a request body before it reaches the API (see artworkTransport).
  front_html?: string;
  back_html?: string;
  front_b64?: string;
  back_b64?: string;
  qr_destination?: string;
  merge_variables?: Record<string, string>;
  from_address?: PostalAddress | null;
}

/*
    What the postal service says about an address.

    `checked` is not `valid`. An account with no live key cannot get a real verdict -- Lob's
    sandbox answers every address with a canned "undeliverable" -- so it reports that it did
    not check rather than inventing an answer. Showing "undeliverable" for an address nobody
    checked teaches people to ignore the field, which costs more than the field is worth.
*/
export interface VerifiedAddress {
  checked: boolean;
  deliverability: string;
  standardized: string | null;
  valid?: boolean;
  // The building is deliverable but the unit may not be: the card reaches a lobby rather
  // than a person, and nothing else about the send looks wrong.
  risky?: boolean;
  note: string;
  // The corrected parts, so the UI can offer them instead of making somebody retype.
  components?: Partial<PostalAddress>;
}

export interface TestSendResult {
  address: VerifiedAddress;
  cost: { per_piece: string; currency: string; source: string; charged: boolean };
  proof: {
    mailpiece_id: string;
    pdf_url: string;
    expected_delivery_date: string | null;
    live: boolean;
    mailed: boolean;
  };
}

export interface DeliveryPerformanceDay {
  day: string;
  sent: number;
  delivered: number;
  returned: number;
  failed: number;
  scans: number;
  cost: number;
}

export interface DeliveryPerformance {
  launch_id: number;
  channel: string;
  totals: DeliveryPerformanceDay;
  days: DeliveryPerformanceDay[];
}

export interface EmailAccountState {
  // True when SES can still only email verified addresses. The commonest reason a test send
  // fails, and the least obvious from SES's own wording.
  sandbox: boolean;
  sending_enabled: boolean;
  enforcement_status: string;
  max_24_hour: number | null;
  sent_last_24_hours: number | null;
  max_send_rate: number | null;
}

export interface TestEmailResult {
  message_id: string;
  to: string;
  subject: string;
  sandbox: boolean;
  unsubscribe_url: string;
}

export interface TestSmsResult {
  sid: string;
  to: string;
  from_number: string;
  status: string;
  // Twilio's own count, which is the one being charged for. One curly quote in a 160
  // character message makes this 3.
  segments: number;
  body: string;
}

export interface SmsPreview {
  characters: number;
  segments: number;
  // True when a non-GSM-7 character has forced the whole message to UCS-2, dropping the
  // per-segment budget from 160 characters to 70.
  unicode: boolean;
  remaining: number;
}

export interface MailEstimate {
  pieces: number;
  cost: string;
  rate: string;
  rate_source: string;
  excluded: {
    no_address: number;
    suppressed: number;
    recently_mailed: number;
    already_mailed: number;
  };
  holdout: number;
  // Null when the engagement has no contract, so there is no ceiling to compare against.
  budget_remaining: string | null;
  within_budget: boolean | null;
}

export interface MailGroup {
  people: number;
  converted: number;
  // Null, not zero, for an empty group: no data is not a 0% conversion rate.
  rate: number | null;
}

export interface MailConversions {
  launch_id: number;
  scanned: MailGroup;
  not_scanned: MailGroup;
  mailed: MailGroup;
  holdout: MailGroup;
  // Percentage points between the mailed group and the holdout. Null without a holdout.
  lift_points: number | null;
  scan_rate: number | null;
}

export interface Suppression {
  id: number;
  organization_id: number;
  // Which channel this entry stops. An address stops mail; an email or phone stops both
  // email and SMS, so one request writes two rows.
  channel: string;
  reason: string;
  note: string;
  created_on: string;
  // Never the identifier itself, only a one-way hash of it. Named to match the server: this
  // was `address_hash` here and `value_hash` there, which read as undefined and said nothing.
  value_hash: string;
}

/*
    A batch of mail somebody decided to post.

    Mail is the one channel with no governor of its own: an ad set with no money stops
    serving, but postcards are priced, printed and posted in one pass and the first sign of an
    overspend is an invoice for post that has already arrived. So a send carries what it will
    cost before it costs it, and the name of the person who agreed to that.
*/
export interface MailSend {
  id: number;
  name: string;
  state: string;
  state_label: string;
  // Null means "goes with the next publish", which is what mail did before there was
  // anywhere to say otherwise.
  scheduled_date: string | null;
  estimated_pieces: number;
  estimated_cost: string;
  actual_pieces: number;
  actual_cost: string;
  approved_by_name: string | null;
  approved_at: string | null;
  approved_over_budget: boolean;
  over_budget_by: string;
  budget_remaining_at_approval: string | null;
  // Whether nothing has reached the printer yet, so this can still be changed or called off.
  is_open: boolean;
  estimated_delivery: string | null;
}

export interface MailSendList {
  // `remaining` is null when nothing caps this engagement. None and zero mean different
  // things: no contract means no ceiling to be over, and presenting one would train people
  // to click through a warning that never meant anything.
  budget: { remaining: string | null; spent_on_mail: string; currency: string };
  sends: MailSend[];
}

export interface LaunchDeliveryConfig {
  id: number;
  launch_id: number;
  channel: string;
  is_enabled: boolean;
  provider_account_id: number | null;
  from_address: Record<string, string>;
  // What will actually be printed: this campaign's address, or the organisation's when the
  // campaign has none. `from_address` stays the campaign's own, so the UI can tell an
  // inherited address from one typed here.
  sender_address?: Record<string, string>;
  // Whose address that is: "campaign", "organization" or "platform". Shown on the card,
  // because a default nobody can see is one that goes out unnoticed on a thousand pieces.
  sender_address_source?: "campaign" | "organization" | "platform" | "none";
  is_ready: boolean;
  // Days a person is left alone after one piece, across this client's campaigns. 0 is no gap.
  min_days_between_pieces: number;
  // SMS only. The hours texts may go out in, and those hours in words.
  send_window?: { start_hour?: number; end_hour?: number; timezone?: string };
  send_window_label?: string;
}

export class DeliveryService {
  async listAccounts(organizationId?: number): Promise<ProviderAccount[]> {
    const query = organizationId ? `?organization_id=${organizationId}` : "";
    return await httpRequest<ProviderAccount[]>(`${BASE}accounts/${query}`, { method: "GET" });
  }

  async createAccount(payload: {
    organization_id: number;
    name: string;
    provider?: string;
    api_key?: string;
  }): Promise<ProviderAccount> {
    return await httpRequest<ProviderAccount>(`${BASE}accounts/`, {
      method: "POST",
      body: JSON.stringify({ provider: "lob", ...payload }),
    });
  }

  async updateAccount(
    accountId: number,
    payload: {
      name?: string;
      api_key?: string;
      is_active?: boolean;
      from_address?: PostalAddress;
    },
  ): Promise<ProviderAccount> {
    return await httpRequest<ProviderAccount>(`${BASE}accounts/${accountId}/`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  // Tells Lob to send this environment's events to us, and stores the signing secret
  // server-side. Until this has run for an account, Lob sends nothing back at all.
  async connectWebhook(accountId: number, replace = false): Promise<{
    connected: boolean;
    webhook_id: string | null;
    created: boolean;
    message: string;
  }> {
    return await httpRequest(`${BASE}accounts/${accountId}/webhook/`, {
      method: "POST",
      body: JSON.stringify({ replace }),
    });
  }

  // `live` is the only thing here that spends money or posts paper, so callers pass it
  // explicitly and the UI asks again before setting it.
  /**
   * Check an address without sending anything.
   *
   * A lookup: no paper, no postage, nothing printed. Reachable on its own because the moment
   * it is useful is while somebody is still typing, not after they have committed to a card.
   */
  async verifyAddress(accountId: number, to: PostalAddress): Promise<VerifiedAddress> {
    return await httpRequest<VerifiedAddress>(
      `${BASE}accounts/${accountId}/verify-address/`,
      { method: "POST", body: JSON.stringify({ to }) },
    );
  }

  async testSend(
    accountId: number,
    payload: { to: PostalAddress; piece: TestSendPiece; live: boolean },
  ): Promise<TestSendResult> {
    return await httpRequest<TestSendResult>(`${BASE}accounts/${accountId}/test-send/`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Mail's own numbers: delivered, returned, scans. Read from our database rather than
  // Lob's, which deletes mailpiece data after 90 days.
  async launchPerformance(launchId: number, channel = "direct_mail"): Promise<DeliveryPerformance> {
    return await httpRequest<DeliveryPerformance>(
      `${BASE}launches/${launchId}/performance/?channel=${channel}`,
      { method: "GET" },
    );
  }

  // A scan is never a conversion. This is the cut that makes the per-recipient QR code worth
  // printing: who scanned, who converted, and how the mailed group compares with the holdout.
  // What publishing would put in the post. The same count the send's budget guard uses, so
  // a publish is never refused with a figure that disagrees with what was on screen.
  // One real email to an address typed into a box. Not a preview: it goes through SES, gets
  // DKIM signed, and lands in a real inbox with a working unsubscribe link.
  async testEmail(
    accountId: number,
    payload: {
      to: string;
      ad_id?: number | null;
      launch_id?: number | null;
      subject?: string;
      preheader?: string;
      html_b64?: string;
      text?: string;
      from_address?: { name: string; email: string } | null;
      sender_name?: string;
    },
  ): Promise<TestEmailResult> {
    return await httpRequest<TestEmailResult>(`${BASE}accounts/${accountId}/test-email/`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // One real text to a number typed into a box. No account needed: until A2P registration
  // completes per client, sending goes through the platform's Twilio credentials, and
  // requiring an account would block testing on the setup step this exists to test.
  async testSms(payload: {
    to: string;
    body?: string;
    ad_id?: number | null;
    organizationId?: number | null;
  }): Promise<TestSmsResult> {
    const { organizationId, ...rest } = payload;
    const query = organizationId ? `?organization_id=${organizationId}` : "";
    return await httpRequest<TestSmsResult>(`${BASE}test-sms/${query}`, {
      method: "POST",
      body: JSON.stringify(rest),
    });
  }

  // What a message will cost before sending it. Worth a round trip because the answer is not
  // the character count: one curly quote drops the per-segment budget from 160 to 70.
  async smsPreview(body: string): Promise<SmsPreview> {
    return await httpRequest<SmsPreview>(`${BASE}sms-preview/`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
  }

  async emailAccountState(accountId: number): Promise<EmailAccountState> {
    return await httpRequest<EmailAccountState>(`${BASE}accounts/${accountId}/email-state/`, {
      method: "GET",
    });
  }

  async mailEstimate(launchId: number): Promise<MailEstimate> {
    return await httpRequest<MailEstimate>(`${BASE}launches/${launchId}/estimate/`, {
      method: "GET",
    });
  }

  async mailConversions(launchId: number): Promise<MailConversions> {
    return await httpRequest<MailConversions>(`${BASE}launches/${launchId}/conversions/`, {
      method: "GET",
    });
  }

  async listSuppressions(organizationId: number): Promise<Suppression[]> {
    return await httpRequest<Suppression[]>(
      `${BASE}suppressions/?organization_id=${organizationId}`,
      { method: "GET" },
    );
  }

  // Exactly one identifier. Which one decides the channel: an address stops mail, an email
  // or a phone number stops email and SMS together, because somebody asking to be left alone
  // means on every channel rather than the one they happened to use to ask.
  async addSuppression(payload: {
    organization_id: number;
    address?: {
      address_line1: string;
      address_line2?: string;
      address_city: string;
      address_state: string;
      address_zip: string;
    };
    email?: string;
    phone?: string;
    reason?: string;
    note?: string;
  }): Promise<Suppression> {
    return await httpRequest<Suppression>(`${BASE}suppressions/`, {
      method: "POST",
      body: JSON.stringify({ reason: "requested", note: "", ...payload }),
    });
  }

  async removeSuppression(suppressionId: number): Promise<{ removed: boolean }> {
    return await httpRequest<{ removed: boolean }>(`${BASE}suppressions/${suppressionId}/`, {
      method: "DELETE",
    });
  }

  async listLaunchChannels(launchId: number): Promise<LaunchDeliveryConfig[]> {
    return await httpRequest<LaunchDeliveryConfig[]>(`${BASE}launches/${launchId}/channels/`, {
      method: "GET",
    });
  }

  async toggleChannel(
    launchId: number,
    channel: string,
    isEnabled: boolean,
  ): Promise<LaunchDeliveryConfig> {
    return await httpRequest<LaunchDeliveryConfig>(
      `${BASE}launches/${launchId}/channels/${channel}/`,
      { method: "PATCH", body: JSON.stringify({ is_enabled: isEnabled }) },
    );
  }

  async configureChannel(
    launchId: number,
    channel: string,
    payload: {
      provider_account_id?: number;
      from_address?: PostalAddress;
      min_days_between_pieces?: number;
    },
  ): Promise<LaunchDeliveryConfig> {
    return await httpRequest<LaunchDeliveryConfig>(
      `${BASE}launches/${launchId}/channels/${channel}/config/`,
      { method: "PATCH", body: JSON.stringify(payload) },
    );
  }

  async listSends(launchId: number): Promise<MailSendList> {
    return await httpRequest<MailSendList>(`${BASE}launches/${launchId}/sends/`);
  }

  async prepareSend(
    launchId: number,
    payload: { name?: string; scheduled_date?: string | null } = {},
  ): Promise<MailSend> {
    return await httpRequest<MailSend>(`${BASE}launches/${launchId}/sends/`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  /**
   * Put a name to the cost.
   *
   * `acceptOverBudget` is a second, deliberate yes rather than part of the first. A single
   * confirm covering both would make "yes" mean two things -- agreeing to a price, and
   * agreeing to exceed a budget -- and only one of those is worth interrupting somebody for.
   * Without it an over-budget send comes back 409 with both figures in the message.
   */
  async approveSend(
    launchId: number,
    sendId: number,
    acceptOverBudget = false,
  ): Promise<MailSend> {
    return await httpRequest<MailSend>(
      `${BASE}launches/${launchId}/sends/${sendId}/approve/`,
      { method: "POST", body: JSON.stringify({ accept_over_budget: acceptOverBudget }) },
    );
  }

  async cancelSend(launchId: number, sendId: number): Promise<MailSend> {
    return await httpRequest<MailSend>(
      `${BASE}launches/${launchId}/sends/${sendId}/cancel/`,
      { method: "POST" },
    );
  }

  async updateSend(
    launchId: number,
    sendId: number,
    payload: { name?: string; scheduled_date?: string | null },
  ): Promise<MailSend> {
    return await httpRequest<MailSend>(`${BASE}launches/${launchId}/sends/${sendId}/`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }
}
